import { useState, useRef, useEffect } from "react";
import { useTeam } from "@/hooks/useTeam";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { InviteMemberDialog } from "./InviteMemberDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Users, UserPlus, Trash2, Mail, Clock, Crown, Shield, Pencil } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function TeamMembersPanel() {
  const { team, members, invitations, myRole, isOwner, isAdmin, loading, refetch } = useTeam();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (editingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [editingName]);

  const startEditingName = () => {
    if (!isOwner || !team) return;
    setNameValue(team.name);
    setEditingName(true);
  };

  const cancelEditingName = () => {
    setEditingName(false);
    setNameValue("");
  };

  const saveTeamName = async () => {
    if (!team || !nameValue.trim() || nameValue.trim() === team.name) {
      cancelEditingName();
      return;
    }
    setSavingName(true);
    const { error } = await supabase
      .from("teams")
      .update({ name: nameValue.trim() })
      .eq("id", team.id);
    setSavingName(false);
    if (error) {
      toast({ title: "Error", description: "Failed to update team name.", variant: "destructive" });
    } else {
      toast({ title: "Team name updated" });
      refetch();
    }
    setEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveTeamName();
    } else if (e.key === "Escape") {
      cancelEditingName();
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    // Fetch member's previous subscription info before deleting
    const member = members.find((m) => m.id === memberId);
    let previousStatus = "free";
    let previousPlan: string | null = null;

    if (member) {
      const { data: memberRecord } = await supabase
        .from("team_members")
        .select("previous_subscription_status, previous_subscription_plan")
        .eq("id", memberId)
        .single();

      if (memberRecord) {
        previousStatus = memberRecord.previous_subscription_status || "free";
        previousPlan = memberRecord.previous_subscription_plan || null;
      }
    }

    const { error } = await supabase
      .from("team_members")
      .delete()
      .eq("id", memberId);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to remove member.",
        variant: "destructive",
      });
    } else {
      // Restore removed user's previous subscription status
      if (member) {
        await supabase
          .from("profiles")
          .update({ 
            subscription_status: previousStatus, 
            subscription_plan: previousPlan,
          })
          .eq("id", member.user_id);
      }
      toast({ title: "Member removed", description: `${memberName} has been removed from the team.` });
      refetch();
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    const { error } = await supabase
      .from("team_invitations")
      .delete()
      .eq("id", invitationId);

    if (!error) {
      toast({ title: "Invitation canceled" });
      refetch();
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "owner": return <Crown className="h-3.5 w-3.5" />;
      case "admin": return <Shield className="h-3.5 w-3.5" />;
      default: return null;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "owner": return "default";
      case "admin": return "secondary";
      default: return "outline";
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">Loading...</div>;
  }

  if (!team) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Users className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-xl font-semibold mb-2">No team yet</h3>
        <p className="text-sm text-muted-foreground mb-1">
          Create a team to collaborate with others.
        </p>
        <p className="text-xs text-muted-foreground mb-6">
          Subscribe to a Team plan to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {editingName ? (
            <Input
              ref={nameInputRef}
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={handleNameKeyDown}
              onBlur={cancelEditingName}
              disabled={savingName}
              className="h-8 text-base font-semibold w-48"
            />
          ) : (
            <div className="flex items-center gap-1.5 group">
              <h3 className="text-base font-semibold">{team.name}</h3>
              {isOwner && (
                <button
                  onClick={startEditingName}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted"
                >
                  <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              )}
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            {members.length} / {team.seat_count} seats used
          </p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Member
          </Button>
        )}
      </div>

      {/* Members List */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Members</p>
        {members.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary shrink-0">
                {(member.profiles?.name?.[0] || member.profiles?.email?.[0] || "?").toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {member.profiles?.name || member.profiles?.email || "Unknown"}
                </p>
                {member.profiles?.name && (
                  <p className="text-xs text-muted-foreground truncate">{member.profiles.email}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant={getRoleBadgeVariant(member.role) as "default" | "secondary" | "outline"} className="gap-1 capitalize text-xs">
                {getRoleIcon(member.role)}
                {member.role}
              </Badge>
              {isAdmin && member.role !== "owner" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove member?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {member.profiles?.name || member.profiles?.email} will lose access to team rooms and their subscription will be deactivated.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => handleRemoveMember(member.id, member.profiles?.name || member.profiles?.email || "")}
                      >
                        Remove
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Pending Invitations</p>
          {invitations.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{inv.email}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Expires {new Date(inv.expires_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className="text-xs capitalize">{inv.role}</Badge>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => handleCancelInvitation(inv.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        teamId={team.id}
        onInviteSent={refetch}
      />
    </div>
  );
}
